import { useEffect, useMemo } from "react";

import { motion } from "framer-motion";
import { Lock, Shield, Star } from "lucide-react";

import { Button } from "../../common/customDesignedComponents/button";
import { getObjLength } from "../../helpers/utils";

const formatPlanMoney = (cents: number | string | undefined | null) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "0.00";
  return (n / 100).toFixed(2);
};

// paid_at_closing = the agent pays at closing; buyer_at_closing = the buyer pays
// through escrow at closing (C.A.R. in-escrow only); card = charge now.
export type ServicerPaymentChoice = "paid_at_closing" | "card" | "buyer_at_closing";

type SubscriptionPlansTailwindProps = {
  subscriptionPlans: any;
  isEnhancedMonitoring?: boolean;
  isFirstYearFreePricing?: boolean;
  isSignupCompleted?: boolean;
  onActivatePlan: (plan: any) => void;
  showServicerPaymentChoice?: boolean;
  paymentChoice?: ServicerPaymentChoice;
  onPaymentChoiceChange?: (choice: ServicerPaymentChoice) => void;
  pocRecording?: boolean;
  onPocRecordingChange?: (value: boolean) => void;
  closingQuote?: any;
  closingQuoteLoading?: boolean;
  closingQuoteError?: string;
  onContinuePaidAtClosing?: (plan: any) => void;
  carEnabled?: boolean;
  carTerm?: string;
  onCarTermChange?: (term: string) => void;
  revenueChoice?: string;
  onRevenueChoiceChange?: (choice: string) => void;
  // The agent's 5% choice. Hidden when the homeowner buys from an invitation.
  showRevenueChoice?: boolean;
};

const SubscriptionPlansTailwind = ({
  subscriptionPlans,
  isEnhancedMonitoring = false,
  isFirstYearFreePricing = false,
  isSignupCompleted = false,
  onActivatePlan,
  showServicerPaymentChoice = false,
  paymentChoice = "card",
  onPaymentChoiceChange,
  pocRecording = false,
  onPocRecordingChange,
  closingQuote = null,
  closingQuoteLoading = false,
  closingQuoteError = "",
  onContinuePaidAtClosing,
  carEnabled = false,
  carTerm = "car_5yr",
  onCarTermChange,
  revenueChoice = "keep",
  onRevenueChoiceChange,
  showRevenueChoice = true,
}: SubscriptionPlansTailwindProps) => {
  const planName = isEnhancedMonitoring ? "Enhanced" : "Yearly";
  const planLabel = isEnhancedMonitoring ? "Enhanced" : "Yearly";

  const selectedPlan = useMemo(() => {
    if (!getObjLength(subscriptionPlans) || !subscriptionPlans?.success) {
      return null;
    }
    return subscriptionPlans?.data?.find((p: any) => p?.name === planName);
  }, [subscriptionPlans, planName]);

  const setupFeeDisplay = useMemo(() => {
    if (!selectedPlan) {
      return "99.99";
    }

    const setupFees = Number(
      selectedPlan?.setup_fees_actual || selectedPlan?.setup_fees,
    );
    if (!Number.isFinite(setupFees)) {
      return "99.99";
    }

    // get-plans returns setup_fees in dollars (e.g. 99.99), not cents.
    return setupFees.toFixed(2);
  }, [selectedPlan]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const activateLabel =
    isFirstYearFreePricing && !isSignupCompleted
      ? "Complete Registration"
      : "Activate Plan";

  const paidAtClosing =
    showServicerPaymentChoice &&
    (paymentChoice === "paid_at_closing" || paymentChoice === "buyer_at_closing");
  const closingTotal = closingQuote?.total ?? null;
  const closingTerm = closingQuote?.term_months ?? 60;
  let inEscrowCommission = false;
  try {
    inEscrowCommission =
      JSON.parse(localStorage.getItem("carPortfolioPrefill") || "{}")
        .enrollment_kind === "in_escrow";
  } catch {
    inEscrowCommission = false;
  }
  const closingRecording = closingQuote?.recording_charge ?? null;

  return (
    <div className="tailwind_component max-w-5xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden"
      >
        <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2">
          <Lock className="h-4 w-4 text-white" />
          <span className="text-white font-medium text-sm">
            Secure connection
          </span>
        </div>

        <div className="p-8 md:p-10">
          {/* Who pays comes first, before the term and revenue share (card 3853). */}
          {showServicerPaymentChoice ? (
            <div className="max-w-xl mx-auto mb-6">
              <p className="mb-3 text-sm font-medium text-foreground">
                How is this protection being paid for?
              </p>
              <div
                className={`grid gap-3 ${
                  inEscrowCommission ? "sm:grid-cols-3" : "sm:grid-cols-2"
                }`}
              >
                {[
                  {
                    choice: "paid_at_closing" as ServicerPaymentChoice,
                    title: "Bill me at closing",
                    note: inEscrowCommission
                      ? `${closingTerm}-month coverage, paid from the agent's commission at closing.`
                      : `${closingTerm}-month coverage, invoiced to you on Net 30 terms. No card needed.`,
                  },
                  {
                    choice: "card" as ServicerPaymentChoice,
                    title: inEscrowCommission ? "Charge Me Now" : "Client pays now",
                    note: "Charge the client's card during this setup.",
                  },
                  // The buyer pays through escrow: in-escrow enrollments only (card 3853).
                  ...(inEscrowCommission
                    ? [
                        {
                          choice: "buyer_at_closing" as ServicerPaymentChoice,
                          title: "Buyer to Pay at Closing",
                          note: "Charge the buyer at closing through escrow.",
                        },
                      ]
                    : []),
                ].map((option) => (
                  <button
                    key={option.choice}
                    type="button"
                    onClick={() => onPaymentChoiceChange?.(option.choice)}
                    className={`rounded-xl border-2 p-4 text-left transition-colors ${
                      paymentChoice === option.choice
                        ? "border-secondary bg-secondary/5"
                        : "border-border bg-background hover:bg-muted/40"
                    }`}
                  >
                    <span className="block text-sm font-semibold text-foreground">
                      {option.title}
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {option.note}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {carEnabled ? (
            <div className="max-w-xl mx-auto mb-6 space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium text-foreground">
                  Protection term
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ["car_1yr", "1 year", "$149"],
                    ["car_3yr", "3 year", "$374"],
                    ["car_5yr", "5 year", "$489"],
                  ].map(([id, label, price]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onCarTermChange?.(id)}
                      className={`rounded-xl border-2 p-3 text-left ${
                        carTerm === id
                          ? "border-secondary bg-secondary/5"
                          : "border-border bg-background"
                      }`}
                    >
                      <span className="block text-sm font-semibold">{label}</span>
                      <span className="block text-xs text-muted-foreground">
                        {price} + $99 setup
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              {showRevenueChoice ? (
              <div>
                <p className="mb-2 text-sm font-medium text-foreground">
                  Your 5% revenue share
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => onRevenueChoiceChange?.("keep")}
                    className={`rounded-xl border-2 p-3 text-left text-sm ${
                      revenueChoice === "keep"
                        ? "border-secondary bg-secondary/5"
                        : "border-border bg-background"
                    }`}
                  >
                    <span className="block font-semibold">Keep 5%</span>
                    <span className="block text-xs text-muted-foreground">
                      Client pays the full package price.
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onRevenueChoiceChange?.("discount")}
                    className={`rounded-xl border-2 p-3 text-left text-sm ${
                      revenueChoice === "discount"
                        ? "border-secondary bg-secondary/5"
                        : "border-border bg-background"
                    }`}
                  >
                    <span className="block font-semibold">Discount the client</span>
                    <span className="block text-xs text-muted-foreground">
                      Take 5% off the package. You earn nothing on this enrollment.
                    </span>
                  </button>
                </div>
              </div>
              ) : null}
            </div>
          ) : null}

          <div className="max-w-xl mx-auto rounded-xl border-2 border-border bg-background p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <p className="text-xs text-muted-foreground">EquityProtect</p>
                <p className="text-lg font-semibold text-foreground">
                  {carEnabled
                    ? `${carTerm === "car_1yr" ? "1" : carTerm === "car_3yr" ? "3" : "5"} year protection`
                    : paidAtClosing
                      ? "5 year paid at closing"
                      : planLabel}
                </p>
              </div>
              <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-border bg-muted/30">
                <Shield
                  className="h-7 w-7 text-emerald-600"
                  strokeWidth={1.75}
                />
                <Star className="pointer-events-none absolute bottom-0.5 right-0.5 h-3 w-3 fill-amber-400 text-amber-500" />
              </div>
            </div>

            <hr className="mb-4 border-border" />

            {paidAtClosing ? (
              <>
                <div className="space-y-1 text-center sm:text-left">
                  <p className="text-4xl font-bold text-secondary">
                    {closingQuoteLoading || closingTotal == null ? (
                      <span className="text-2xl font-semibold text-muted-foreground">
                        Calculating…
                      </span>
                    ) : (
                      <>
                        $<span data-cs-encrypt>{closingTotal}</span>
                        <span className="text-lg font-semibold text-foreground">
                          {" "}
                          due at closing
                        </span>
                      </>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground pt-2">
                    {closingTerm} months of Complete Protection.{" "}
                    {carEnabled
                      ? carTerm === "car_5yr"
                        ? "Includes the $99 setup fee. No sales tax."
                        : "Includes the $99 setup fee and sales tax."
                      : "No setup fee, no sales tax."}{" "}
                    No card charged today.
                  </p>
                </div>

                <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-muted/30 p-4">
                  <input
                    type="checkbox"
                    checked={pocRecording}
                    onChange={(event) =>
                      onPocRecordingChange?.(event.target.checked)
                    }
                    className="mt-0.5 h-4 w-4 accent-[#0e7490]"
                  />
                  <span className="text-sm">
                    <span className="block font-medium text-foreground">
                      Recording fee paid outside of closing (POC)
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {pocRecording
                        ? "Recording deposit removed from the client's total — you cover it."
                        : `Recording deposit stays in the total${
                            closingRecording ? ` ($${closingRecording})` : ""
                          }.`}
                    </span>
                  </span>
                </label>

                {closingQuoteError ? (
                  <p className="mt-3 text-sm text-red-700">
                    {closingQuoteError}
                  </p>
                ) : null}

                <div className="flex justify-center pt-8">
                  <Button
                    onClick={() =>
                      selectedPlan && onContinuePaidAtClosing?.(selectedPlan)
                    }
                    disabled={
                      !selectedPlan || closingQuoteLoading || closingTotal == null
                    }
                    className="bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-semibold px-12 py-3 text-sm rounded-full shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide"
                  >
                    Continue
                  </Button>
                </div>
              </>
            ) : carEnabled ? (
              <>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <span>Subscription</span>
                    <span>${closingQuote?.sub_amount ?? "—"}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>Setup fee</span>
                    <span>${closingQuote?.setup_fees ?? "—"}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>Sales tax</span>
                    <span>${closingQuote?.sales_tax ?? "—"}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>Recording fee</span>
                    <span>${closingQuote?.recording_charge ?? "—"}</span>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-border pt-3 text-base font-semibold">
                    <span>Total Due Today</span>
                    <span>
                      {closingQuoteLoading || closingQuote?.total == null
                        ? "Calculating…"
                        : `$${closingQuote.total}`}
                    </span>
                  </div>
                </div>
                {closingQuoteError ? (
                  <p className="mt-3 text-sm text-red-700">{closingQuoteError}</p>
                ) : null}
                <div className="flex justify-center pt-8">
                  <Button
                    onClick={() => selectedPlan && onActivatePlan(selectedPlan)}
                    disabled={!selectedPlan || closingQuoteLoading || closingQuote?.total == null}
                    className="bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-semibold px-12 py-3 text-sm rounded-full shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide"
                  >
                    {activateLabel}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1 text-center sm:text-left">
                  <p className="text-4xl font-bold text-secondary">
                    $
                    <span data-cs-encrypt>
                      {formatPlanMoney(selectedPlan?.recurring_amount)}
                    </span>
                    <span className="text-lg font-semibold text-foreground">
                      /year
                    </span>
                  </p>
                  {!isEnhancedMonitoring && (
                    <>
                      <p className="text-base font-semibold text-foreground pt-2">
                        ${setupFeeDisplay} one-time setup fee
                      </p>
                      <p className="text-xs text-muted-foreground pt-2">
                        + applicable sales tax
                      </p>
                      {selectedPlan?.recording_fee ? (
                        <p className="text-xs text-muted-foreground">
                          + applicable Deposit for recording fee (One time fee)
                        </p>
                      ) : null}
                    </>
                  )}
                </div>

                <div className="flex justify-center pt-8">
                  <Button
                    onClick={() => selectedPlan && onActivatePlan(selectedPlan)}
                    disabled={!selectedPlan}
                    className="bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-semibold px-12 py-3 text-sm rounded-full shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wide"
                  >
                    {activateLabel}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SubscriptionPlansTailwind;
